import { Link } from "react-router-dom"
import { useHead } from "@unhead/react"
import {ArrowRight, Calculator, Building2, Landmark, Beer, Rocket, Star, TrendingUp, Shield, Users, CheckCircle, Zap, } from 'lucide-react'
import LoanCalculator from "@/components/LoanCalculator"
import FAQ from "@/components/FAQ"
import CTASection from "@/components/CTASection"

const features = [
  {
    icon: Building2,
    title: "Franchise Financing",
    description: "Specialized calculators for Subway, McDonald's, and other major franchise brands.",
    link: "/franchise-financing",
    color: "emerald",
  },
  {
    icon: Landmark,
    title: "SBA Loan Calculator",
    description: "Calculate payments for SBA 7(a) and government-backed restaurant loans.",
    link: "/sba-loans",
    color: "sky",
  },
  {
    icon: Beer,
    title: "Bar Financing",
    description: "Dedicated tools for bar and nightclub owners seeking SBA and commercial loans.",
    link: "/bar-financing",
    color: "amber",
  },
  {
    icon: Rocket,
    title: "Startup Guide",
    description: "Everything you need to finance a new restaurant from concept to grand opening.",
    link: "/startup-guide",
    color: "violet",
  },
]

const stats = [
  { value: "$2.5B+", label: "Loans Calculated", icon: Calculator },
  { value: "50,000+", label: "Restaurant Owners Served", icon: Users },
  { value: "98%", label: "Accuracy Rate", icon: TrendingUp },
  { value: "5 Min", label: "Average Time to Estimate", icon: Zap },
]

const trustBadges = [
  "SBA Approved Lender Network",
  "FDIC Insured Partners",
  "A+ BBB Rating",
  "256-bit SSL Encrypted",
]

export default function Home() {
  useHead({
    title: "Restaurant Loan Calculator | Free Payment Estimator",
    meta: [
      {
        name: "description",
        content: "Calculate restaurant loan payments instantly. Free tools for SBA loans, franchise financing, equipment loans & startup costs.",
      },
      { name: "keywords", content: "restaurant business loan calculator, restaurant financing options, franchise loan calculator, SBA loan for restaurant, restaurant equipment financing" },
      { property: "og:title", content: "Restaurant Loan Calculator | Free Payment Estimator" },
      { property: "og:description", content: "Calculate restaurant loan payments instantly. Free tools for SBA loans, franchise financing, equipment loans & startup costs." },
      { property: "og:image", content: "https://www.restaurantloancalculator.com/og-home.jpg" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Restaurant Loan Calculator | Free Payment Estimator" },
      { name: "twitter:description", content: "Calculate restaurant loan payments instantly. Free tools for SBA loans, franchise financing, equipment loans & startup costs." },
      { name: "twitter:image", content: "https://www.restaurantloancalculator.com/og-home.jpg" },
    ],
    link: [
      { rel: "canonical", href: "https://www.restaurantloancalculator.com/" },
    ],
    script: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Restaurant Loan Calculator",
          "url": "https://www.restaurantloancalculator.com",
          "description": "Free restaurant loan calculators for franchise, SBA, bar, and startup financing.",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://www.restaurantloancalculator.com/?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How much does it cost to open a restaurant?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The average cost to open a restaurant ranges from $175,000 to $750,000 depending on location, size, and concept."
              }
            },
            {
              "@type": "Question",
              "name": "What is an SBA 7(a) loan for restaurants?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "An SBA 7(a) loan is a government-backed loan up to $5 million with competitive rates, designed for small businesses including restaurants."
              }
            }
          ]
        }),
      },
    ],
  })

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://static.lumi.new/8e/8e4c534439c64010901aef95f380fed3.jpg"
            alt="Restaurant financing background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/90 via-[#0A1628]/80 to-[#0A1628]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6 opacity-0 animate-fade-in-up">
              <Star className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">
                #1 Restaurant Loan Calculator
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight opacity-0 animate-fade-in-up animation-delay-100">
              Precision Restaurant Loan Calculators for{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-emerald-300">
                Every Business Type
              </span>
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-slate-300 leading-relaxed max-w-2xl opacity-0 animate-fade-in-up animation-delay-200">
              Stop guessing your startup costs. Use our niche-specific calculators for Subway, McDonald's, Bars, and New Startups to get accurate monthly payment estimates and find the best lending options for your culinary dream.
            </p>

            <div className="flex flex-wrap gap-4 mt-8 opacity-0 animate-fade-in-up animation-delay-300">
              <a
                href="#calculator"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm rounded-xl transition-all duration-200 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 hover:-translate-y-0.5"
              >
                <Calculator className="w-4 h-4" />
                Calculate Now
              </a>
              <Link
                to="/franchise-financing"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/5 hover:bg-white/10 text-white font-semibold text-sm rounded-xl border border-white/10 hover:border-white/20 transition-all duration-200"
              >
                Explore Franchise Loans
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-10 opacity-0 animate-fade-in-up animation-delay-400">
              {trustBadges.map((badge) => (
                <div key={badge} className="flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="text-xs text-slate-400 font-medium">{badge}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative z-10 -mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="bg-[#0F2240]/80 backdrop-blur-sm rounded-xl p-5 border border-white/5 text-center hover:border-emerald-500/20 transition-colors"
              >
                <stat.icon className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{stat.value}</p>
                <p className="text-xs text-slate-400 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
              <Calculator className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Interactive Tool</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">Restaurant Business Loan Calculator</h2>
            <p className="text-slate-400 mt-3 max-w-xl mx-auto">
              Select your financing type and adjust the sliders to get instant, accurate payment estimates.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <LoanCalculator />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 sm:py-24 bg-[#0C1B30]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">Restaurant Financing Options</h2>
            <p className="text-slate-400 mt-3 max-w-xl mx-auto">
              Explore specialized calculators and guides for every type of restaurant and bar business.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => (
              <Link
                key={feature.title}
                to={feature.link}
                className="group bg-[#0F2240]/60 rounded-xl p-6 border border-white/5 hover:border-emerald-500/20 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-11 h-11 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-4 group-hover:bg-emerald-500/15 transition-colors">
                  <feature.icon className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">{feature.description}</p>
                <span className="inline-flex items-center gap-1 text-emerald-400 text-sm font-medium group-hover:gap-2 transition-all">
                  Learn More <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">How It Works</h2>
            <p className="text-slate-400 mt-3">Three simple steps to estimate your restaurant loan payments.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: "01", title: "Select Your Loan Type", desc: "Choose from franchise, SBA, equipment, bar, or startup loan presets tailored to your business." },
              { step: "02", title: "Adjust Your Numbers", desc: "Use the interactive sliders to fine-tune the loan amount, interest rate, and term length." },
              { step: "03", title: "Get Your Estimate", desc: "Instantly see your monthly payment, total interest, and total cost of the loan." },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-emerald-400 font-bold text-lg">{item.step}</span>
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Links Section */}
      <section className="py-16 sm:py-20 bg-[#0C1B30]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">Popular Financing Guides</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { label: "Subway Franchise Financing Guide", to: "/franchise-financing" },
              { label: "McDonald's Franchise Loan Options", to: "/franchise-financing" },
              { label: "SBA 7(a) Loans for Restaurants", to: "/sba-loans" },
              { label: "SBA Loans for Bars & Nightclubs", to: "/bar-financing" },
              { label: "Restaurant Equipment Financing", to: "/sba-loans" },
              { label: "New Restaurant Startup Loans", to: "/startup-guide" },
            ].map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="flex items-center justify-between px-5 py-4 bg-[#0F2240]/60 rounded-xl border border-white/5 hover:border-emerald-500/20 transition-all group"
              >
                <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">{item.label}</span>
                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FAQ />
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <CTASection />
      </section>
    </div>
  )
}
